/**
 * Koordinator Dashboard Layout — thin wrapper over AppDashboardShell.
 * App-local concerns: nav items, sidebar content, branding, route-conditional sidebar visibility.
 *
 * Navigation Structure:
 *   OVERVIEW: Dashboard, Analytics
 *   SALES: BrandStores, Proposals, Orders, Earnings
 *   ADMIN: Invite, Settings
 *   SUPPORT: Agent C, Instructions
 *
 * Agent C is integrated into the shared panel system (search/filters/agent-c modes).
 * Search + filter state is managed here and passed to both panel content and pages.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppDashboardShell,
  AgentCPanelContent,
  type NavbarItemConfig,
  type RightPanelMode,
  cn,
} from '@bright-web-skeleton/ui';
import { SearchFiltersPanelContent } from '../panels/SearchFiltersPanelContent';

import { useAuth } from '../../shared/contexts';
import { supabase } from '../../lib/supabase';
import { resolveBrandForUser } from '../../lib/resolveBrand';
import {
  Store,
  Tag,
  Package,
  ShoppingCart,
  LogOut,
  User,
  Bell,
  Home,
  Compass,
  PlusCircle,
  DollarSign,
  Sparkles,
  BookOpen,
  LayoutDashboard,
  BarChart3,
  Settings,
  UserPlus,
} from 'lucide-react';

const navbarItems: NavbarItemConfig[] = [
  { label: 'Home', path: '/home', icon: Home },
  { label: 'Discover', path: '/discover', icon: Compass },
  { label: 'Create', path: '/proposals/create', icon: PlusCircle },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  { label: 'Account', icon: User, action: 'toggle-sidebar' },
];

interface BrandStoreEntry {
  id: string;
  display_name: string | null;
  country_code: string;
  status: string;
  is_default: boolean;
}

interface TaxonomyIndustry {
  id: string;
  label: string;
  slug: string;
}

interface TaxonomyCategory {
  id: string;
  label: string;
  slug: string;
  industry_id: string;
}

export const DashboardLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isStoreRoute = location.pathname === '/store' || location.pathname.startsWith('/store/');

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightPanel, setRightPanel] = useState<RightPanelMode>('none');
  const [token, setToken] = useState<string | null>(null);

  // ─── Search + Filter state (lifted, persists across panel open/close) ───
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // ─── Taxonomy data ───
  const [industries, setIndustries] = useState<TaxonomyIndustry[]>([]);
  const [categories, setCategories] = useState<TaxonomyCategory[]>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);

  // Load taxonomy once
  useEffect(() => {
    const load = async () => {
      try {
        const [indRes, catRes] = await Promise.all([
          supabase.from('taxonomy_industries').select('id, label, slug').order('label'),
          supabase
            .from('taxonomy_categories')
            .select('id, label, slug, industry_id')
            .order('label'),
        ]);
        setIndustries(indRes.data || []);
        setCategories(catRes.data || []);
      } catch {
        // non-critical — filters will show empty
      } finally {
        setTaxonomyLoading(false);
      }
    };
    load();
  }, []);

  // Filter handlers
  const handleToggleIndustry = useCallback(
    (id: string) => {
      setSelectedIndustryIds((prev) => {
        const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        // Clear categories that no longer belong to selected industries
        if (!next.includes(id)) {
          setSelectedCategoryIds((cats) =>
            cats.filter((catId) => {
              const cat = categories.find((c) => c.id === catId);
              return cat ? next.includes(cat.industry_id) : false;
            }),
          );
        }
        return next;
      });
    },
    [categories],
  );

  const handleToggleCategory = useCallback((id: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedIndustryIds([]);
    setSelectedCategoryIds([]);
  }, []);

  // Session token for copilot
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setToken(data.session?.access_token ?? null));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, s) => setToken(s?.access_token ?? null));
    return () => subscription.unsubscribe();
  }, []);

  // Brand + store state
  const [stores, setStores] = useState<BrandStoreEntry[]>([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [brandHandle, setBrandHandle] = useState<string | null>(null);

  // A successful Brand / BrandStore delete can happen without remounting this
  // layout. Remove the retired row immediately so Create/sidebar navigation
  // cannot keep showing a stale active BrandStore until a hard refresh.
  useEffect(() => {
    const handleBrandStoresChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ kind?: 'brand' | 'store'; id?: string }>).detail;
      if (detail?.kind === 'store' && detail.id) {
        setStores((current) => current.filter((store) => store.id !== detail.id));
        return;
      }
      if (detail?.kind === 'brand') {
        setStores([]);
      }
    };

    window.addEventListener('kloutsale:brandstores-changed', handleBrandStoresChanged);
    return () =>
      window.removeEventListener('kloutsale:brandstores-changed', handleBrandStoresChanged);
  }, []);

  // Load stores + brand handle for sidebar
  useEffect(() => {
    if (!user?.id || storesLoaded) return;
    const load = async () => {
      try {
        const resolved = await resolveBrandForUser(user.id);
        if (!resolved?.brandId) return;

        const [storeRes, brandRes] = await Promise.all([
          supabase
            .from('brand_stores')
            .select('id, display_name, country_code, status, is_default')
            .is('retired_at', null)
            .eq('brand_id', resolved.brandId)
            .order('is_default', { ascending: false }),
          supabase.from('brands').select('handle').eq('id', resolved.brandId).maybeSingle(),
        ]);

        setStores(storeRes.data || []);
        setBrandHandle(brandRes.data?.handle || null);
      } catch {
        // non-critical
      } finally {
        setStoresLoaded(true);
      }
    };
    load();
  }, [user?.id, storesLoaded]);

  const storeCount = stores.length;

  const isPathActive = useCallback(
    (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  const handleNavigate = useCallback(
    (path: string) => {
      setSidebarOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const handleRightPanelChange = useCallback((panel: RightPanelMode) => {
    setRightPanel(panel);
    if (panel === 'account') setSidebarOpen(false);
  }, []);

  const handleSidebarLinkClick = useCallback(
    (path: string) => {
      navigate(path);
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
        setRightPanel('none');
      }
    },
    [navigate],
  );

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/login');
  }, [signOut, navigate]);

  const handleStoreClick = useCallback(() => {
    handleSidebarLinkClick('/store');
  }, [handleSidebarLinkClick]);

  // handleManageClick removed — store management is accessed via BrandStoresPage [Manage] button,
  // not from the sidebar. Brand identity management is at /account/:handle.

  // Open Agent C via panel system (not floating)
  const handleOpenAgentC = useCallback(() => {
    setRightPanel('agent-c');
  }, []);

  // Derive Agent C context from current route
  const copilotContext = useMemo(() => {
    if (
      location.pathname.startsWith('/proposals/') &&
      location.pathname !== '/proposals/create' &&
      location.pathname !== '/proposals/katalog-matches'
    ) {
      const id = location.pathname.split('/proposals/')[1]?.split('/')[0];
      if (id)
        return {
          type: 'proposal_katalog_match',
          label: 'Proposal matching',
          params: { proposalId: id },
        };
    }
    if (location.pathname === '/proposals') return { type: 'general', label: 'Proposals' };
    return null;
  }, [location.pathname]);

  // Agent C panel content (rendered inside the unified panel system)
  const agentCContent = useMemo(
    () => (
      <AgentCPanelContent
        role="brand"
        supabaseUrl={import.meta.env.VITE_SUPABASE_URL}
        accessToken={token}
        context={copilotContext}
        suggestedPrompts={[
          'Which katalogs should I send this proposal to?',
          'Which creators should I work with?',
          'Where should I expand shipping?',
          'What is blocking my ROI?',
        ]}
      />
    ),
    [token, copilotContext],
  );

  const sidebarContent = (
    <>
      <nav className="flex-1 py-2 px-3 overflow-y-auto" aria-label="Sidebar navigation">
        <ul className="space-y-1" role="list">
          {/* OVERVIEW */}
          <li className="pt-0">
            <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none">
              OVERVIEW
            </h3>
          </li>
          <li>
            <button
              onClick={() => handleSidebarLinkClick('/command-center')}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                isPathActive('/command-center')
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <LayoutDashboard className="w-5 h-5 shrink-0" /> Command Center
            </button>
          </li>
          <li>
            <button
              onClick={() => handleSidebarLinkClick('/analytics')}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                isPathActive('/analytics')
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <BarChart3 className="w-5 h-5 shrink-0" /> Analytics
            </button>
          </li>

          {/* SALES */}
          <li className="pt-4">
            <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none">
              SALES
            </h3>
          </li>

          {[
            { label: 'BrandStores', route: '/brandstores', icon: Store },
            { label: 'Products', route: '/products', icon: Package },
            { label: 'Proposals', route: '/proposals', icon: Tag },
            { label: 'Orders', route: '/orders', icon: ShoppingCart },
            { label: 'Earnings', route: '/account/earnings', icon: DollarSign },
          ].map((item) => (
            <li key={item.route}>
              <button
                onClick={() => handleSidebarLinkClick(item.route)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                  isPathActive(item.route)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}
              >
                <item.icon className="w-5 h-5 shrink-0" /> {item.label}
              </button>
            </li>
          ))}

          {/* ADMIN */}
          <li className="pt-4">
            <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none">
              ADMIN
            </h3>
          </li>

          <li>
            <button
              onClick={() => handleSidebarLinkClick('/admin/invite')}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                isPathActive('/admin/invite')
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <UserPlus className="w-5 h-5 shrink-0" /> Invite
            </button>
          </li>
          <li>
            <button
              onClick={() => handleSidebarLinkClick('/account/settings')}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                isPathActive('/account/settings')
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <Settings className="w-5 h-5 shrink-0" /> Account Settings
            </button>
          </li>

          {/* SUPPORT */}
          <li className="pt-4">
            <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none">
              SUPPORT
            </h3>
          </li>
          <li>
            <button
              onClick={handleOpenAgentC}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                rightPanel === 'agent-c'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <Sparkles className="w-5 h-5 shrink-0" />
              <span>Agent C</span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                AI
              </span>
            </button>
          </li>
          <li>
            <button
              onClick={() => handleSidebarLinkClick('/instructions')}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                isPathActive('/instructions')
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <BookOpen className="w-5 h-5 shrink-0" /> Instructions
            </button>
          </li>
        </ul>
      </nav>

      <div className="py-4 px-3 border-t border-border mt-auto">
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogOut className="w-5 h-5 shrink-0" /> Sign Out
        </button>
      </div>

      <div className="p-3 border-t border-border">
        <button
          onClick={() => navigate('/kloutforce-info')}
          className="w-full text-center hover:bg-muted p-2 rounded-lg transition-colors"
        >
          <p className="text-sm font-medium text-primary">Kloutforce</p>
          <p className="text-xs text-muted-foreground">info@kloutforce.com</p>
        </button>
      </div>
    </>
  );

  const logo = (
    <Link
      to="/command-center"
      className="flex items-center gap-2 no-underline"
      onClick={() => {
        setSidebarOpen(false);
        setRightPanel('none');
      }}
    >
      <img
        src="/brand/kloutsale-logo.png"
        alt="Kloutsale"
        className="h-8 w-8 rounded-lg object-contain"
      />
      <span className="text-lg font-semibold text-foreground hidden sm:inline">Kloutsale</span>
    </Link>
  );

  const accountContent = sidebarContent;

  const searchFiltersContent = useMemo(
    () => (
      <SearchFiltersPanelContent
        searchQuery={searchQuery}
        onSearchCommit={setSearchQuery}
        industries={industries}
        categories={categories}
        taxonomyLoading={taxonomyLoading}
        selectedIndustryIds={selectedIndustryIds}
        selectedCategoryIds={selectedCategoryIds}
        onToggleIndustry={handleToggleIndustry}
        onToggleCategory={handleToggleCategory}
        onClearAll={handleClearAllFilters}
      />
    ),
    [
      searchQuery,
      industries,
      categories,
      taxonomyLoading,
      selectedIndustryIds,
      selectedCategoryIds,
      handleToggleIndustry,
      handleToggleCategory,
      handleClearAllFilters,
    ],
  );

  return (
    <AppDashboardShell
      logo={logo}
      navItems={navbarItems}
      onNavigate={handleNavigate}
      isPathActive={isPathActive}
      fixedHeader={false}
      sidebar={!isStoreRoute ? sidebarContent : undefined}
      sidebarOpen={sidebarOpen}
      onSidebarClose={() => setSidebarOpen(false)}
      accountContent={accountContent}
      searchContent={searchFiltersContent}
      agentCContent={agentCContent}
      rightPanel={rightPanel}
      onRightPanelChange={handleRightPanelChange}
      rightPanelTitle={rightPanel === 'account' ? user?.name || 'Brand User' : undefined}
      mainClassName="bg-katalog-surface"
    >
      {children || <Outlet />}
    </AppDashboardShell>
  );
};

export default DashboardLayout;
